// hooks/useColorScheme.js
// Minimal Hauler-only theme controller.
//
// - Persists preference in localStorage as "loaddash_hauler_theme"
// - Sets data-theme on document.documentElement so haulerTheme.css
//   can swap CSS variables
// - Defaults to prefers-color-scheme on first visit (no stored value)
// - Cleans up the data-theme attribute when the Hauler shell unmounts
//   so /drivers and other surfaces don't inherit Hauler styling
//
// Scoped to Hauler. /drivers uses its own per-component themeMode
// state (DriverApp.js:270) and is not coupled to this hook.

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "loaddash_hauler_theme";

function readInitial() {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "dark" || stored === "light") return stored;
  } catch (_) { /* ignore quota / privacy mode */ }
  // Respect OS preference on first visit
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches) {
    return "light";
  }
  return "dark";
}

export function useColorScheme() {
  const [theme, setTheme] = useState(readInitial);

  // Mirror state to <html data-theme="..."> so CSS variables apply.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const prev = root.getAttribute("data-theme");
    root.setAttribute("data-theme", theme);
    return () => {
      // Only remove the attribute if we set it. Avoids clobbering
      // any other surface that might set its own data-theme later.
      if (root.getAttribute("data-theme") === theme) {
        if (prev) root.setAttribute("data-theme", prev);
        else root.removeAttribute("data-theme");
      }
    };
  }, [theme]);

  // Persist
  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, theme); } catch (_) {}
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme(t => (t === "dark" ? "light" : "dark"));
  }, []);

  return { theme, setTheme, toggle, isLight: theme === "light", isDark: theme === "dark" };
}
